import React from 'react';
import { Facility } from '../../services/api/domains/facilities/types';
import { modalStyles as styles } from './styles';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import {
  facilityIdField,
  facilityAlwaysDisabledFields,
  getFacilityFields,
} from './configs/facilityFields';
import { Manufacturer } from 'services/api/domains/manufacturers/types';

interface FacilityModalProps {
  facility: Facility;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedFacility: Facility) => void;
  close: () => void;
  allManufacturers: Manufacturer[];
}

const FacilityModal: React.FC<FacilityModalProps> = ({
  facility,
  initialMode = 'VIEW',
  save,
  close,
  allManufacturers,
}) => {
  // Use the hook for all modal logic
  const {
    editedEntity: editedFacility,
    setEditedEntity: setEditedFacility,
    modalMode,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getSaveButtonStyle,
    getSaveButtonHoverStyle,
    getSaveButtonFocusStyle,
    getCloseButtonStyle,
    getCloseButtonHoverStyle,
    getCloseButtonFocusStyle,
    getSaveButtonText,
    getCloseButtonText,
  } = useEditableModal({
    entity: facility,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: facilityAlwaysDisabledFields,
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={styles.modalTitleStyle}>
            {modalMode === 'EDIT' ? 'Edit Facility' : 'Facility Details'}
          </h2>
          <button
            onClick={handleClose}
            style={styles.modalCloseXStyle}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXStyle)
            }>
            ×
          </button>
        </div>

        <div style={styles.modalBodyStyle}>
          {/* Facility ID field if present */}
          {facility.facility_id && (
            <ModalFieldRenderer
              fields={[facilityIdField]}
              entity={editedFacility}
              setEntity={setEditedFacility}
              mode={modalMode}
              focusedField={focusedField}
              setFocusedField={setFocusedField}
              isFieldDisabled={isFieldDisabled}
              getFloatingLabelStyle={getFloatingLabelStyle}
              getFloatingInputStyle={getFloatingInputStyle}
              styles={styles}
              missingMessage="Unknown"
            />
          )}

          {/* All facility fields */}
          <ModalFieldRenderer
            fields={getFacilityFields({
              manufacturers: allManufacturers,
            })}
            entity={editedFacility}
            setEntity={setEditedFacility}
            mode={modalMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            isFieldDisabled={isFieldDisabled}
            getFloatingLabelStyle={getFloatingLabelStyle}
            getFloatingInputStyle={getFloatingInputStyle}
            styles={styles}
            missingMessage="Unknown"
          />
        </div>

        <div style={styles.modalFooterStyle}>
          <button
            onClick={handleClose}
            style={getCloseButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getCloseButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getCloseButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }>
            {getCloseButtonText()}
          </button>
          <button
            onClick={handleSave}
            style={getSaveButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getSaveButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getSaveButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }>
            {getSaveButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(FacilityModal);
export type { ModalMode };
